/**
 * CadastroMfaPage (`/cadastro-mfa`, RN-M1-001, ADR-021, ADR-052, V4) — cadastro do
 * TOTP no primeiro acesso de qualquer perfil (ou depois de o admin redefinir o
 * autenticador).
 *
 * O Cognito emitiu o token RESTRITO (sem perfil) porque o usuário ainda não tem
 * TOTP. Aqui: `AssociateSoftwareToken` → QR `otpauth://` (e o segredo para
 * digitação manual) → o usuário digita o primeiro código → `VerifySoftwareToken` +
 * TOTP como fator preferido → a sessão restrita é encerrada e o usuário entra de
 * novo, agora com o desafio TOTP. Nenhum segredo ou código é logado.
 */

import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { CadastroTotp, useAuthActions } from '../contexts/AuthContext';
import { useWhiteLabel } from '../contexts/WhiteLabelContext';

/** Mensagem exibida no /login depois do cadastro concluído. */
export const AVISO_TOTP_CADASTRADO =
  'Verificação em duas etapas ativada. Entre novamente e informe o código do aplicativo.';

export function CadastroMfaPage(): JSX.Element {
  const { iniciarCadastroTotp, confirmarCadastroTotp } = useAuthActions();
  const navigate = useNavigate();
  const whiteLabel = useWhiteLabel();

  const [cadastro, setCadastro] = useState<CadastroTotp | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    let ativo = true;
    iniciarCadastroTotp(whiteLabel.systemName)
      .then(async (dados) => {
        // SVG (não canvas): funciona em qualquer navegador e sem dependência de canvas.
        const svg = await QRCode.toString(dados.uri, { type: 'svg', margin: 1, width: 220 });
        const imagem = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
        if (ativo) {
          setCadastro(dados);
          setQr(imagem);
        }
      })
      .catch(() => {
        if (ativo) {
          setErro('Sessão de cadastro expirada. Entre novamente para cadastrar o aplicativo.');
        }
      });
    return () => {
      ativo = false;
    };
  }, [iniciarCadastroTotp, whiteLabel.systemName]);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      await confirmarCadastroTotp(code.trim());
      navigate('/login', { replace: true, state: { aviso: AVISO_TOTP_CADASTRADO } });
    } catch {
      setErro('Código de verificação inválido ou expirado.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="auth-page" style={{ ['--cor-primaria' as string]: whiteLabel.primaryColor }}>
      <form className="auth-card" onSubmit={onSubmit} aria-labelledby="cadastro-mfa-title">
        <h1 id="cadastro-mfa-title" className="auth-title">
          Ative a verificação em duas etapas
        </h1>
        <p className="auth-subtitle">
          O acesso ao sistema exige um aplicativo autenticador. Leia o QR code com o
          aplicativo e informe o código de 6 dígitos gerado.
        </p>

        {qr ? (
          <img src={qr} alt="QR code para o aplicativo autenticador" className="auth-qr" />
        ) : (
          !erro && <p className="auth-subtitle">Gerando o QR code…</p>
        )}
        {cadastro && (
          <p className="auth-secret">
            Sem câmera? Digite o código no aplicativo: <code>{cadastro.segredo}</code>
          </p>
        )}

        <label className="field">
          <span>Código</span>
          <input
            autoFocus
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          />
        </label>

        {erro && (
          <p className="form-error" role="alert">
            {erro}
          </p>
        )}

        <button type="submit" className="btn btn-primary" disabled={enviando || !cadastro}>
          {enviando ? 'Verificando…' : 'Ativar'}
        </button>
      </form>
    </div>
  );
}
